import React, { useEffect, useState } from 'react';
import { api } from '../../api/client';
import Pagination from './Pagination';
import usePageSize from '../../utils/usePageSize';
import EventContactDetailModal from './EventContactDetailModal';

import { ta, taf, adminLocale } from '../../i18n/adminT';
const emptyFilters = { search: '', evento: '', dateFrom: '', dateTo: '' };

function isEventExpired(dataFine) {
  if (!dataFine) return false;
  const endOfDay = new Date(`${dataFine}T23:59:59`);
  return new Date() > endOfDay;
}

export default function EventContactsManager({ isSuperAdmin }) {
  const [filters, setFilters] = useState(emptyFilters);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const { pageSize, pageSizeRef, setPageSize } = usePageSize('event-contacts');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [actionError, setActionError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [detailContact, setDetailContact] = useState(null);

  const [activeEvent, setActiveEvent] = useState('');
  const [activeEventEnd, setActiveEventEnd] = useState('');
  const [eventDraft, setEventDraft] = useState('');
  const [eventEndDraft, setEventEndDraft] = useState('');
  const [eventSaving, setEventSaving] = useState(false);
  const [eventSaved, setEventSaved] = useState(false);

  const load = async (f = filters, p = page) => {
    setLoading(true);
    try {
      const res = await api.getEventContacts(f, p, pageSizeRef.current);
      // Eliminata l'ultima riga dell'ultima pagina: si torna alla precedente
      if (res.data.length === 0 && p > 1 && res.total > 0) {
        return load(f, p - 1);
      }
      setRows(res.data);
      setTotal(res.total);
      setPage(p);
    } finally {
      setLoading(false);
    }
  };

  const loadEventConfig = async () => {
    try {
      const res = await api.getEventContactConfig();
      const evento = res?.data?.evento || '';
      const dataFine = res?.data?.dataFine || '';
      setActiveEvent(evento);
      setActiveEventEnd(dataFine);
      setEventDraft(evento);
      setEventEndDraft(dataFine);
    } catch {
      // in caso di errore resta vuoto
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(emptyFilters, 1); loadEventConfig(); }, []);

  const updateFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    load(filters, 1);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    load(filters, 1);
  };

  const handleClearFilters = () => {
    setFilters(emptyFilters);
    load(emptyFilters, 1);
  };

  const handleSaveEvent = async () => {
    setActionError('');
    setEventSaving(true);
    setEventSaved(false);
    try {
      await api.saveEventContactConfig({ evento: eventDraft, dataFine: eventEndDraft });
      setActiveEvent(eventDraft);
      setActiveEventEnd(eventEndDraft);
      setEventSaved(true);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setEventSaving(false);
    }
  };

  const handleExport = async () => {
    setActionError('');
    setExporting(true);
    try {
      await api.exportEventContactsCsv(filters);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (contact) => {
    if (!window.confirm(taf('Eliminare il contatto "{nome}"?\n\nL\'operazione non è reversibile.', { nome: `${contact.nome} ${contact.cognome}` }))) return;
    setActionError('');
    setDeletingId(contact.id);
    try {
      await api.deleteEventContact(contact.id);
      await load();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const hasActiveFilters = Object.values(filters).some(Boolean);

  return (
    <div>
      <h1 className="admin-page-title">{ta('Contatti')}</h1>
      <p className="admin-page-subtitle">
        {total} {ta('contatti totali')}{total > pageSize ? taf(' · pagina {page} di {pages}', { page, pages: Math.max(1, Math.ceil(total / pageSize)) }) : ''}
      </p>

      <div className="admin-card" style={{ maxWidth: 480 }}>
        <h3 style={{ marginBottom: 10 }}>{ta('Evento attivo')}</h3>
        <p style={{ fontSize: '0.85rem', color: 'var(--muted-text)', marginBottom: 14 }}>
          {ta('Il nome inserito qui viene applicato automaticamente a tutti i nuovi contatti raccolti dalla pagina')} <span className="mono">{ta('/contatti')}</span>{ta(', finché non lo cambi o non superi la data di fine. Aggiornalo prima di ogni evento per poterli distinguere in seguito. L\'informativa privacy dedicata si configura invece in Impostazioni → Impostazioni contatti.')}
        </p>

        {isEventExpired(activeEventEnd) && (
          <div style={{ background: 'rgba(193,80,61,0.1)', color: 'var(--clay-red-dark)', borderRadius: 'var(--radius-control)', padding: '10px 14px', fontSize: '0.85rem', marginBottom: 16 }}>
            {taf('⚠ L\'evento "{evento}" è terminato il {data}. I nuovi contatti raccolti da quella data non vengono più taggati con questo nome. Imposta il prossimo evento qui sotto.', { evento: activeEvent, data: new Date(`${activeEventEnd}T00:00:00`).toLocaleDateString(adminLocale()) })}
          </div>
        )}

        <div className="field-row">
          <div className="field">
            <label htmlFor="active-event">{ta('Nome evento')}</label>
            <input
              id="active-event"
              type="text"
              value={eventDraft}
              onChange={(e) => { setEventDraft(e.target.value); setEventSaved(false); }}
              placeholder={ta('Es. Fiera del Mobile 2026')}
            />
          </div>
          <div className="field">
            <label htmlFor="active-event-end">{ta('Data di fine')} <span className="optional-tag">{ta('(facoltativa)')}</span></label>
            <input
              id="active-event-end"
              type="date"
              value={eventEndDraft}
              onChange={(e) => { setEventEndDraft(e.target.value); setEventSaved(false); }}
            />
          </div>
        </div>

        {eventSaved && (
          <div style={{ background: 'rgba(62,142,126,0.12)', color: 'var(--teal-green-dark)', borderRadius: 'var(--radius-control)', padding: '10px 14px', fontSize: '0.85rem', marginBottom: 12 }}>
            {ta('Salvato.')}
          </div>
        )}
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSaveEvent}
          disabled={eventSaving}
          style={{ width: 'auto', padding: '10px 20px' }}
        >
          {eventSaving ? ta('Salvataggio…') : ta('Salva')}
        </button>
      </div>

      {actionError && <div className="form-error">{actionError}</div>}

      <form onSubmit={handleFilterSubmit} className="registrations-filters">
        <input
          className="search-input"
          placeholder={ta('Nome, cognome, email o azienda…')}
          value={filters.search}
          onChange={updateFilter('search')}
          style={{ width: 220 }}
        />
        <input
          className="search-input"
          placeholder={ta('Evento…')}
          value={filters.evento}
          onChange={updateFilter('evento')}
          style={{ width: 180 }}
        />
        <label className="filter-date-label">
          {ta('Da')}
          <input type="date" className="search-input" value={filters.dateFrom} onChange={updateFilter('dateFrom')} style={{ width: 150 }} />
        </label>
        <label className="filter-date-label">
          {ta('A')}
          <input type="date" className="search-input" value={filters.dateTo} onChange={updateFilter('dateTo')} style={{ width: 150 }} />
        </label>

        <button type="submit" className="btn btn-primary" style={{ width: 'auto', padding: '10px 20px', marginTop: 0 }}>
          {ta('Filtra')}
        </button>
        {hasActiveFilters && (
          <button type="button" className="btn btn-ghost" style={{ width: 'auto', padding: '10px 16px', marginTop: 0 }} onClick={handleClearFilters}>
            {ta('Pulisci filtri')}
          </button>
        )}
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting}
          className="btn btn-ghost"
          style={{ width: 'auto', padding: '10px 18px', marginTop: 0, border: '1.5px solid var(--border-hair)', borderRadius: 'var(--radius-control)' }}
        >
          {exporting ? ta('Esportazione…') : ta('Esporta CSV')}
        </button>
      </form>

      <div className="admin-card">
        <table className="admin-table">
          <thead>
            <tr>
              <th>{ta('Nome')}</th>
              <th>{ta('Cognome')}</th>
              <th>{ta('Email')}</th>
              <th>{ta('Telefono')}</th>
              <th>{ta('Azienda')}</th>
              <th>{ta('Evento')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.map((c) => (
              <tr key={c.id}>
                <td>
                  <button type="button" className="link-button" onClick={() => setDetailContact(c)} title={ta('Vedi tutti i dati')}>
                    {c.nome}
                  </button>
                </td>
                <td>
                  <button type="button" className="link-button" onClick={() => setDetailContact(c)} title={ta('Vedi tutti i dati')}>
                    {c.cognome}
                  </button>
                </td>
                <td>{c.email}</td>
                <td>{c.telefono || '—'}</td>
                <td>{c.azienda || '—'}</td>
                <td>{c.evento || '—'}</td>
                <td>
                  {isSuperAdmin && (
                    <button
                      className="btn-ghost btn"
                      style={{ width: 'auto', padding: '6px 10px', color: 'var(--clay-red-dark)' }}
                      onClick={() => handleDelete(c)}
                      disabled={deletingId === c.id}
                    >
                      {deletingId === c.id ? ta('Eliminazione…') : ta('Elimina')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && rows.length === 0 && <p style={{ color: 'var(--muted-text)', padding: '16px 0 0' }}>{ta('Nessun contatto trovato.')}</p>}
        {loading && <p style={{ color: 'var(--muted-text)', padding: '16px 0 0' }}>{ta('Caricamento…')}</p>}

        <Pagination
          page={page}
          totalPages={Math.max(1, Math.ceil(total / pageSize))}
          onChange={(p) => load(filters, p)}
          total={total}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>

      {detailContact && (
        <EventContactDetailModal contact={detailContact} onClose={() => setDetailContact(null)} />
      )}
    </div>
  );
}
